import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Trash2, Download, Loader2, ListChecks } from "lucide-react";
import { C, Panel, PriorityBadge, Empty, downloadCsv } from "./seoUi";
import { errMsg } from "@/lib/api";

const STATUSES = ["open", "in_progress", "done", "ignored"];
const FILTERS = ["all", ...STATUSES];

const Th = ({ children }) => (
  <th className="text-left font-semibold px-3 py-2 text-[12px] uppercase tracking-wide" style={{ color: C.slate }}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-3 py-2 text-[13px] align-top ${className}`} style={{ color: C.charcoal }}>{children}</td>
);

export default function TasksPanel({ franchise, reloadSignal }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (franchise) params.franchise = franchise;
      if (filter !== "all") params.status = filter;
      const { data } = await api.get("/seo-intelligence/tasks", { params });
      setItems(data.items || []);
    } catch { toast.error("Could not load tasks."); }
    finally { setLoading(false); }
  }, [franchise, filter]);
  useEffect(() => { load(); }, [load, reloadSignal]);

  const patch = async (id, body) => {
    try { await api.patch(`/seo-intelligence/tasks/${id}`, body); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await api.delete(`/seo-intelligence/tasks/${id}`); setItems((xs) => xs.filter((x) => x.id !== id)); toast.success("Deleted."); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = () => downloadCsv("seo-action-queue.csv", items.map((t) => ({
    priority: t.priority, status: t.status, issue: t.issue_summary, recommended_fix: t.recommended_fix,
    page_url: t.page_url, territory: t.territory_label, service_lane: t.service_lane, created_at: t.created_at,
  })));

  return (
    <Panel title="Action Queue" subtitle="SEO tasks — created manually or from “Improve this page”." testid="seo-tab-tasks"
      right={<div className="flex items-center gap-2">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="task-filter" className="h-9 w-[140px] text-[13px]"><SelectValue /></SelectTrigger>
          <SelectContent>{FILTERS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f === "all" ? "All statuses" : f.replace("_", " ")}</SelectItem>)}</SelectContent>
        </Select>
        <Button data-testid="tasks-export-btn" size="sm" variant="outline" onClick={exportCsv} className="h-9 gap-1 border-[#D6E0E7]" style={{ color: C.navy }}><Download size={14} /> CSV</Button>
      </div>}>
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="animate-spin" style={{ color: C.navy }} /></div> : items.length ? (
        <div className="overflow-x-auto rounded-lg border" style={{ borderColor: C.line }}>
          <table className="w-full border-collapse">
            <thead><tr style={{ background: "#F4F8FB" }}><Th>Priority</Th><Th>Issue</Th><Th>Recommended fix</Th><Th>Page</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>
              {items.map((t, i) => (
                <tr key={t.id} data-testid={`task-row-${i}`} className="border-t" style={{ borderColor: C.line }}>
                  <Td><PriorityBadge priority={t.priority} /></Td>
                  <Td><span className="text-[13px]">{t.issue_summary}</span>{t.territory_label && <div className="text-[11px]" style={{ color: C.slate }}>{t.territory_label}</div>}</Td>
                  <Td><span className="text-[12.5px]" style={{ color: C.slate }}>{t.recommended_fix || "—"}</span></Td>
                  <Td><span className="font-mono text-[11.5px]" style={{ color: C.sky }}>{t.page_url || "—"}</span></Td>
                  <Td>
                    <Select value={t.status} onValueChange={(v) => patch(t.id, { status: v })}>
                      <SelectTrigger data-testid={`task-status-${i}`} className="h-8 w-[130px] text-[12px]"><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                    </Select>
                  </Td>
                  <Td><button data-testid={`task-del-${i}`} onClick={() => del(t.id)} className="text-[#9C1E22] hover:opacity-70"><Trash2 size={15} /></button></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <Empty><div className="flex flex-col items-center gap-2"><ListChecks size={26} style={{ color: C.sky }} /><span>No tasks yet. Use “Task” buttons on the audit, gaps, or recommendations to queue work here.</span></div></Empty>}
    </Panel>
  );
}
